import { cloneDeepWith } from 'lodash-es'
import type { RumEvent } from '@datadog/browser-rum'
import { redactSecretsInText } from 'lib/helpers/redactSecretsInText/redactSecretsInText'
import { redactUrlSecrets } from 'lib/helpers/redactUrlSecrets/redactUrlSecrets'

interface RedactRumEventParams {
  event: RumEvent
}

const redactOptionalUrl = (url: string | undefined) => (url === undefined ? undefined : redactUrlSecrets({ url }))

const redactOptionalText = (text: string | undefined) =>
  text === undefined ? undefined : redactSecretsInText({ text })

/**
 * The fields are the ones RUM 7.2 lets `beforeSend` modify; the SDK reverts anything else, including
 * `error.causes[]`.
 */
export const redactRumEvent = ({ event }: RedactRumEventParams): void => {
  event.view.url = redactUrlSecrets({ url: event.view.url })
  event.view.referrer = redactOptionalUrl(event.view.referrer)

  if (event.context) {
    event.context = cloneDeepWith(event.context, (value: unknown) =>
      typeof value === 'string' ? redactSecretsInText({ text: value }) : undefined,
    )
  }

  if (event.type === 'view' && event.view.performance?.lcp) {
    event.view.performance.lcp.resource_url = redactOptionalUrl(event.view.performance.lcp.resource_url)
  }

  if (event.type === 'resource') {
    event.resource.url = redactUrlSecrets({ url: event.resource.url })
  }

  if (event.type === 'error') {
    event.error.message = redactSecretsInText({ text: event.error.message })
    event.error.stack = redactOptionalText(event.error.stack)
    event.error.handling_stack = redactOptionalText(event.error.handling_stack)

    if (event.error.resource) {
      event.error.resource.url = redactUrlSecrets({ url: event.error.resource.url })
    }
  }

  if (event.type === 'long_task') {
    event.long_task.scripts?.forEach((script) => {
      script.source_url = redactOptionalUrl(script.source_url)
    })
  }
}
